import React from "react";
import type {
  CashReceiptUsage,
  InvoiceType,
  TaxDocumentBuyer,
} from "../../../../domain/types/tax-document";

const inputClass =
  "w-full h-[40px] px-3 text-[13px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none disabled:bg-[#f5f5f5] disabled:text-[#888]";

interface Props {
  value: TaxDocumentBuyer;
  onChange: (next: TaxDocumentBuyer) => void;
  disabled?: boolean;
  /** 증빙 종류를 바꿀 수 있는지 (발급 전이면 true) */
  allowTypeChange?: boolean;
}

/**
 * 증빙을 받을 구매자 정보 입력.
 *
 * 국세청 필수 항목:
 * - 현금영수증: 거래용도 + 신분확인번호(소득공제=휴대폰 / 지출증빙=사업자번호 또는 휴대폰)
 * - 세금계산서: 사업자번호, 상호, 대표자명, 사업장 주소 (+ 받을 이메일)
 */
export const TaxDocumentBuyerForm: React.FC<Props> = ({ value, onChange, disabled, allowTypeChange = true }) => {
  const set = (patch: Partial<TaxDocumentBuyer>) => onChange({ ...value, ...patch });

  const typeButton = (type: InvoiceType, label: string) => (
    <button
      type="button"
      disabled={disabled || !allowTypeChange}
      onClick={() => set({ invoiceType: type })}
      className={`flex-1 h-[40px] text-[13px] rounded-[8px] border ${
        value.invoiceType === type
          ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4] font-medium"
          : "border-[#e0e0e0] text-[#555] hover:bg-[#fafafa]"
      } disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );

  const usageButton = (usage: CashReceiptUsage, label: string) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => set({ cashReceiptUsage: usage })}
      className={`flex-1 h-[36px] text-[12px] rounded-[6px] border ${
        (value.cashReceiptUsage ?? "INCOME_DEDUCTION") === usage
          ? "border-[#4285F4] text-[#4285F4] font-medium"
          : "border-[#e0e0e0] text-[#555]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col gap-3">
      <Field label="증빙 종류">
        <div className="flex gap-2">
          {typeButton("CASH_RECEIPT", "현금영수증")}
          {typeButton("TAX_INVOICE", "세금계산서")}
        </div>
      </Field>

      {value.invoiceType === "CASH_RECEIPT" ? (
        <>
          <Field label="거래용도">
            <div className="flex gap-2">
              {usageButton("INCOME_DEDUCTION", "소득공제용 (개인)")}
              {usageButton("EXPENSE_PROOF", "지출증빙용 (사업자)")}
            </div>
          </Field>
          <Field
            label={
              (value.cashReceiptUsage ?? "INCOME_DEDUCTION") === "INCOME_DEDUCTION"
                ? "휴대폰번호"
                : "사업자등록번호 또는 휴대폰번호"
            }
            required
          >
            <input
              className={inputClass}
              disabled={disabled}
              inputMode="numeric"
              value={value.identityNum ?? ""}
              onChange={(e) => set({ identityNum: e.target.value })}
              placeholder="- 없이 숫자만"
            />
          </Field>
          <Field label="수신 이메일 (선택)">
            <input
              className={inputClass}
              disabled={disabled}
              value={value.buyerEmail ?? ""}
              onChange={(e) => set({ buyerEmail: e.target.value })}
              placeholder="비우면 주문자 이메일로 보냅니다"
            />
          </Field>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="사업자등록번호" required>
              <input
                className={inputClass}
                disabled={disabled}
                inputMode="numeric"
                value={value.buyerBusinessNumber ?? ""}
                onChange={(e) => set({ buyerBusinessNumber: e.target.value })}
                placeholder="- 없이 10자리"
              />
            </Field>
            <Field label="상호(법인명)" required>
              <input
                className={inputClass}
                disabled={disabled}
                value={value.buyerBusinessName ?? ""}
                onChange={(e) => set({ buyerBusinessName: e.target.value })}
              />
            </Field>
            <Field label="대표자명" required>
              <input
                className={inputClass}
                disabled={disabled}
                value={value.buyerCeoName ?? ""}
                onChange={(e) => set({ buyerCeoName: e.target.value })}
              />
            </Field>
            <Field label="담당자명">
              <input
                className={inputClass}
                disabled={disabled}
                value={value.buyerContactName ?? ""}
                onChange={(e) => set({ buyerContactName: e.target.value })}
                placeholder="비우면 대표자명"
              />
            </Field>
          </div>
          <Field label="사업장 주소" required>
            <input
              className={inputClass}
              disabled={disabled}
              value={value.buyerAddress ?? ""}
              onChange={(e) => set({ buyerAddress: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="업태">
              <input
                className={inputClass}
                disabled={disabled}
                value={value.buyerBizType ?? ""}
                onChange={(e) => set({ buyerBizType: e.target.value })}
              />
            </Field>
            <Field label="종목">
              <input
                className={inputClass}
                disabled={disabled}
                value={value.buyerBizClass ?? ""}
                onChange={(e) => set({ buyerBizClass: e.target.value })}
              />
            </Field>
          </div>
          <Field label="세금계산서 받을 이메일" required>
            <input
              className={inputClass}
              disabled={disabled}
              value={value.buyerEmail ?? ""}
              onChange={(e) => set({ buyerEmail: e.target.value })}
            />
          </Field>
        </>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
  label,
  required,
  children,
}) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[12px] font-medium text-[#555]">
      {label}
      {required && <span className="text-[#4285F4] ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

/** 화면 상태 → 저장 요청. 빈 문자열은 null 로 보낸다 */
export const toBuyerPayload = (value: TaxDocumentBuyer): TaxDocumentBuyer => {
  const clean = (v?: string | null) => (v && v.trim() ? v.trim() : null);
  return {
    invoiceType: value.invoiceType,
    cashReceiptUsage: value.invoiceType === "CASH_RECEIPT" ? value.cashReceiptUsage ?? "INCOME_DEDUCTION" : null,
    identityNum: clean(value.identityNum),
    buyerBusinessNumber: clean(value.buyerBusinessNumber),
    buyerBusinessName: clean(value.buyerBusinessName),
    buyerCeoName: clean(value.buyerCeoName),
    buyerAddress: clean(value.buyerAddress),
    buyerBizType: clean(value.buyerBizType),
    buyerBizClass: clean(value.buyerBizClass),
    buyerContactName: clean(value.buyerContactName),
    buyerEmail: clean(value.buyerEmail),
    buyerMobile: clean(value.buyerMobile),
  };
};
